import { ChevronLeft, ChevronRight } from '@carbon/icons-react';
import { useTheme } from '@repo/theme';
import { HorizBox } from 'component/basics';
import { PaginationButton } from 'component/button/pagination-button';
import { UtilityText } from 'component/text';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { t } from 'util/stdtypes';

export type PaginationRowProps = {
    currentPage: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
};

export function PaginationRow({ currentPage, totalItems, pageSize, onPageChange }: PaginationRowProps) {
    const { colors } = useTheme();
    const totalPages = Math.ceil(totalItems / pageSize);
    const s = PaginationRowStyle;

    if (totalItems === 0) return null;

    const start = currentPage * pageSize + 1;
    const end = Math.min((currentPage + 1) * pageSize, totalItems);

    const pageNumbers = getPageNumbers({ currentPage, totalPages });

    return (
        <HorizBox center spread>
            <UtilityText
                color={colors.colorTextSecondary}
                formatParams={{ start, end, total: totalItems }}
                label={t('pagination_row.showing', 'Showing {start}-{end} of {total}')}
                type='small'
            />
            <HorizBox center gap={16}>
                <PaginationButton
                    ariaLabel={t('pagination_row.previous_page', 'Previous page')}
                    disabled={currentPage === 0}
                    icon={ChevronLeft}
                    onPress={() => onPageChange(currentPage - 1)}
                />
                {pageNumbers.map((entry, i) =>
                    entry === '...' ? (
                        // eslint-disable-next-line react/no-array-index-key
                        <View key={`ellipsis-${i}`} style={s.ellipsis}>
                            <UtilityText color={colors.colorTextSecondary} text='...' type='small' weight='medium' />
                        </View>
                    ) : (
                        <PaginationButton
                            key={entry}
                            onPress={() => onPageChange(entry as number)}
                            page={(entry as number) + 1}
                            selected={entry === currentPage}
                        />
                    ),
                )}
                <PaginationButton
                    ariaLabel={t('pagination_row.next_page', 'Next page')}
                    disabled={currentPage === totalPages - 1}
                    icon={ChevronRight}
                    onPress={() => onPageChange(currentPage + 1)}
                />
            </HorizBox>
        </HorizBox>
    );
}

function getPageNumbers({ currentPage, totalPages }: { currentPage: number; totalPages: number }): (number | '...')[] {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i);
    }

    // Always exactly 7 elements for ≥8 pages
    if (currentPage <= 3) {
        return [0, 1, 2, 3, 4, '...', totalPages - 1];
    }
    if (currentPage >= totalPages - 4) {
        return [0, '...', totalPages - 5, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1];
    }
    return [0, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages - 1];
}

const PaginationRowStyle = StyleSheet.create({
    ellipsis: {
        height: 32,
        minWidth: 32,
        alignItems: 'center',
        justifyContent: 'center',
    },
});
